import {createReducer, on} from '@ngrx/store';
import {
  ClearSideNavWeeksList,
  ClearState,
  FetchResourceListFailure,
  FetchResourceListRequest,
  FetchResourceListSuccess,
  FetchSideNavWeeksListFailure,
  FetchSideNavWeeksListRequest,
  FetchSideNavWeeksListSuccess,
  FetchWeeksListFailure,
  FetchWeeksListRequest,
  FetchWeeksListSuccess,
  FetchWeekSummaryFailure,
  FetchWeekSummaryRequest,
  FetchWeekSummarySuccess,
  UpdateDailyScoreFailure,
  UpdateDailyScoreRequest,
  UpdateDailyScoreSuccess
} from "./behavior-tracker.actions";
import {MetricOptionModel} from "../../../../models/metric-option.model";
import {QuestionnaireModel} from "../../../../models/questionnaire.model";
import {SideNavWeekList} from "../../../../models/side-nav-week-list";
import {ResourceList} from "../../../../models/resource-list";
import {WeekList} from "../../../../models/week-list";

// State for this feature (User)
export interface BehaviorTrackerState {
  metricsData: {};
  metricsOptions: MetricOptionModel[]
  sideNavWeeksList: SideNavWeekList,
  groupInfo: {},
  weekListInfo: WeekList,
  questionnairesList: {
    list: QuestionnaireModel[],
    loading: boolean
  },
  resourceList: ResourceList,
  summary: {
    weekSummary: {}
    loading: boolean
  },
  dailyScoreInfo: {
    loading: boolean
  }
}

const initialState: BehaviorTrackerState = {
  metricsData: {},
  metricsOptions: [],
  sideNavWeeksList: {
    list: [],
    loading: false,
    total: 0,
    pageNumber: 0,
    limit: 0,
    hasNext: false,
    hasPrev: false
  },
  groupInfo: {},
  weekListInfo: {
    list: [],
    total: 0,
    pageNumber: 0,
    limit: 0,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  questionnairesList: {
    list: [],
    loading: false
  },
  resourceList: {
    list: [],
    loading: false,
    total: 0,
    pageNumber: 0,
    limit: 0,
    hasNext: false,
    hasPrev: false
  },
  summary: {
    weekSummary: {},
    loading: false
  },
  dailyScoreInfo: {
    loading: false
  }
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, () => initialState),

  // FETCH SIDE NAV WEEKS LIST
  on(FetchSideNavWeeksListRequest, (state, props) => ({
    ...state,
    sideNavWeeksList: {
      ...state.sideNavWeeksList,
      list: [
        ...(
          props.payload?.pageNumber ?
          props.payload?.pageNumber === 1 ? [] : [...state.sideNavWeeksList.list] :
            []
        )
      ],
      loading: true
    }
  })),
  on(FetchSideNavWeeksListSuccess, (state, props) => ({
    ...state,
    sideNavWeeksList: {
      ...props.sideNavWeeksList,
      list: [
        ...(props.sideNavWeeksList.pageNumber === 1 ? [
          ...props.sideNavWeeksList.list
        ] : [
          ...state.sideNavWeeksList.list,
          ...props.sideNavWeeksList.list
        ])
      ],
      loading: false
    }
  })),
  on(FetchSideNavWeeksListFailure, (state) => ({
    ...state,
    sideNavWeeksList: {
      ...initialState.sideNavWeeksList,
      loading: false
    }
  })),

  // FETCH WEEKS LIST
  on(FetchWeeksListRequest, (state, props) => ({
    ...state,
    groupInfo: initialState.groupInfo,
    weekListInfo: {
      ...state.weekListInfo,
      list: [
        ...(
          props.payload?.pageNumber ?
          props.payload?.pageNumber === 1 ? [] : [...state.weekListInfo.list] :
            []
        )
      ],
      loading: true
    }
  })),
  on(FetchWeeksListSuccess, (state, props) => ({
    ...state,
    groupInfo: props.groupInfo,
    weekListInfo: {
      ...props.weeksListInfo,
      list: [
        ...(props.weeksListInfo.pageNumber === 1 ? [
          ...props.weeksListInfo.list
        ] : [
          ...state.weekListInfo.list,
          ...props.weeksListInfo.list
        ])
      ],
      loading: false
    }
  })),
  on(FetchWeeksListFailure, (state) => ({
    ...state,
    groupInfo: initialState.groupInfo,
    weekListInfo: {
      ...initialState.weekListInfo,
      loading: false
    }
  })),

  // FETCH RESOURCE LIST
  on(FetchResourceListRequest, (state, props) => ( {
    ...state,
    resourceList: {
      ...initialState.resourceList,
      list: [
        ...(
          props.payload?.pageNumber ?
          props.payload?.pageNumber === 1 ? [] : [...state.resourceList.list] :
            []
        )
      ],
      loading: true
    }
  })),
  on(FetchResourceListSuccess, (state, props) => ({
    ...state,
    resourceList: {
      ...props.resourceList,
      list: [
        ...(props.resourceList.pageNumber === 1 ? [
          ...props.resourceList.list
        ] : [
          ...state.resourceList.list,
          ...props.resourceList.list
        ])
      ],
      loading: false
    }
  })),
  on(FetchResourceListFailure, (state) => ({
    ...state,
    resourceList: {
      ...initialState.resourceList,
      loading: false
    }
  })),

// FETCH WEEK SUMMARY
  on(FetchWeekSummaryRequest, (state) => ({
    ...state,
    summary: {
      weekSummary: {},
      loading: true
    }
  })),
  on(FetchWeekSummarySuccess, (state, props) => ({
    ...state,
    summary: {
      weekSummary: props.weekSummary,
      loading: false
    }
  })),
  on(FetchWeekSummaryFailure, (state) => ({
    ...state,
    summary: {
      weekSummary: {},
      loading: false
    }
  })),

  // CLEAR SIDE NAV WEEKS LIST
  on(ClearSideNavWeeksList, (state) => ({
    ...state,
    sideNavWeeksList: initialState.sideNavWeeksList
  })),

  // UPDATE DAILY SCORE
  on(UpdateDailyScoreRequest, (state) => ({
    ...state,
    dailyScoreInfo: {
      loading: true
    },
    summary: {
      ...initialState.summary,
      loading: true
    }
  })),
  on(UpdateDailyScoreSuccess, (state, props) => ({
    ...state,
    dailyScoreInfo: {
      loading: false
    },
    summary: {
      weekSummary: props.weekSummary,
      loading: false
    }
  })),
  on(UpdateDailyScoreFailure, (state) => ({
    ...state,
    dailyScoreInfo: {
      loading: false
    },
    summary: {
      ...initialState.summary,
      loading: false
    }
  })),
);
